import React, { createContext, useContext, useReducer, useMemo } from 'react';

// Create separate contexts for state and actions
const SecondarySidebarStateContext = createContext();
const SecondarySidebarActionsContext = createContext();

// Initial state
const initialState = {
  isOpen: false,
  title: '',
  content: null,
  className: 'right-sidebar-md' // Default class
};

// Reducer function
const secondarySidebarReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_SECONDARY_SIDEBAR':
      return {
        ...state,
        isOpen: true,
        title: action.payload.title,
        content: action.payload.content,
        className: action.payload.className || state.className
      };
    case 'CLOSE_SECONDARY_SIDEBAR':
      return {
        ...state,
        isOpen: false,
        title: '',
        content: null,
        className: state.className
      };
    case 'SET_SECONDARY_CLASS':
      return {
        ...state,
        className: action.payload
      };
    default:
      return state;
  }
};

// Provider component
export const SecondarySidebarProvider = ({ children }) => {
  const [state, dispatch] = useReducer(secondarySidebarReducer, initialState);

  // Memoize actions
  const actions = useMemo(() => ({
    openSecondarySidebar: (payload) => dispatch({ type: 'OPEN_SECONDARY_SIDEBAR', payload }),
    closeSecondarySidebar: () => dispatch({ type: 'CLOSE_SECONDARY_SIDEBAR' }),
    setSecondaryClass: (className) => dispatch({ type: 'SET_SECONDARY_CLASS', payload: className }),
    toggleSecondarySidebar: (payload) => {
      if (state.isOpen) {
        dispatch({ type: 'CLOSE_SECONDARY_SIDEBAR' });
      } else {
        dispatch({ type: 'OPEN_SECONDARY_SIDEBAR', payload });
      }
    }
  }), [state.isOpen]);

  // Memoize state
  const sidebarState = useMemo(() => state, [state]);

  return (
    <SecondarySidebarStateContext.Provider value={sidebarState}>
      <SecondarySidebarActionsContext.Provider value={actions}>
        {children}
      </SecondarySidebarActionsContext.Provider>
    </SecondarySidebarStateContext.Provider>
  );
};

// Custom hooks
export const useSecondarySidebarState = () => {
  const context = useContext(SecondarySidebarStateContext);
  if (context === undefined) {
    throw new Error('useSecondarySidebarState must be used within a SecondarySidebarProvider');
  }
  return context;
};

export const useSecondarySidebarActions = () => {
  const context = useContext(SecondarySidebarActionsContext);
  if (context === undefined) {
    throw new Error('useSecondarySidebarActions must be used within a SecondarySidebarProvider');
  }
  return context;
};

// Combined hook for backward compatibility
export const useSecondarySidebar = () => {
  const state = useSecondarySidebarState();
  const actions = useSecondarySidebarActions();
  return {
    ...state,
    ...actions
  };
};

export default SecondarySidebarProvider; 